import { useEffect, useMemo, useState } from "react";
import { Modal, Spin, Tooltip } from "antd";
import { Eye } from "lucide-react";
import LoadingOverlay from "../shared/LoadingOverlay";
import Toast from "../shared/Toast";
import { getAccessToken } from "../../services/authApi";
import {
    fieldReader,
    fetchLoanMilestones,
    fetchLoanConditions,
    addLoanCondition,
    addConditionTracking,
    updateLoanMilestone
} from "../../services/loanApi";
import { error as logAppInsightError } from "../../utils/logger";
import { logError } from "../../utils/logError";
import { generateAdditionalDocuments } from "../approvalcertificate/approvalCertificateHelper";
import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/legacy/build/pdf.worker.min.mjs";

pdfjsLib.GlobalWorkerOptions.workerSrc = "";

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;

const CONDITION_TITLE_LOCK_VALIDATION = "Lock Validation Required";
const MILESTONE_CONDITIONAL_APPROVAL = "Conditional Approval";
const MILESTONE_CTC = "CTC";

function isConditionOpenByStatus(status) {
    const normalized = (status || "").trim().toLowerCase();

    return [
        "added",
        "requested",
        "received",
        "reviewed",
        "re-opened",
        "reopened",
        "pending"
    ].includes(normalized);
}

function getActivePTDs(conditions) {
    return (conditions || []).filter(
        (c) =>
            !c?.isRemoved &&
            (c?.priorTo || "").trim() === "Docs" &&
            (c?.statusOpen === true || isConditionOpenByStatus(c?.status))
    );
}

function getLockValidationCondition(conditions) {
    return (conditions || []).find(
        (c) =>
            !c?.isRemoved &&
            (c?.priorTo || "").trim() === "Docs" &&
            (c?.title || "").trim().toLowerCase() === CONDITION_TITLE_LOCK_VALIDATION.trim().toLowerCase()
    );
}

function findMilestoneByName(milestones, name) {
    return (milestones || []).find((m) => (m?.name || "").trim() === name);
}

function buildDecision(completedMilestoneName, hasOpenPTD, needsLockValidation) {
    if (completedMilestoneName === MILESTONE_CONDITIONAL_APPROVAL) {
        if (hasOpenPTD && needsLockValidation) {
            return {
                docType: "conditional",
                ensureLockValidationCondition: true,
                completeMilestones: [MILESTONE_CONDITIONAL_APPROVAL]
            };
        }

        if (hasOpenPTD && !needsLockValidation) {
            return {
                docType: "conditional",
                ensureLockValidationCondition: false,
                completeMilestones: [MILESTONE_CONDITIONAL_APPROVAL]
            };
        }

        if (!hasOpenPTD && needsLockValidation) {
            return {
                docType: null,
                ensureLockValidationCondition: true,
                completeMilestones: [MILESTONE_CONDITIONAL_APPROVAL]
            };
        }

        if (!hasOpenPTD && !needsLockValidation) {
            return {
                docType: "final",
                ensureLockValidationCondition: false,
                completeMilestones: [MILESTONE_CTC]
            };
        }
    }

    if (completedMilestoneName === MILESTONE_CTC) {
        if (hasOpenPTD && needsLockValidation) {
            return {
                docType: "conditional",
                ensureLockValidationCondition: true,
                completeMilestones: [MILESTONE_CONDITIONAL_APPROVAL]
            };
        }

        if (hasOpenPTD && !needsLockValidation) {
            return {
                docType: "conditional",
                ensureLockValidationCondition: false,
                completeMilestones: [MILESTONE_CONDITIONAL_APPROVAL]
            };
        }

        if (!hasOpenPTD && needsLockValidation) {
            return {
                docType: null,
                ensureLockValidationCondition: true,
                completeMilestones: [MILESTONE_CONDITIONAL_APPROVAL]
            };
        }

        if (!hasOpenPTD && !needsLockValidation) {
            return {
                docType: "final",
                ensureLockValidationCondition: false,
                completeMilestones: [MILESTONE_CTC]
            };
        }
    }

    return {
        docType: null,
        ensureLockValidationCondition: false,
        completeMilestones: []
    };
}

function buildActionSummary(completedMilestoneName, hasOpenPTD, needsLockValidation) {
    const decision = buildDecision(completedMilestoneName, hasOpenPTD, needsLockValidation);
    const actions = [];

    if (!completedMilestoneName) {
        return ["Milestone name not available."];
    }

    if (decision.ensureLockValidationCondition) {
        actions.push('Create PTD condition "Lock Validation Required", or re-open it if already cleared.');
    }

    if (decision.docType === "conditional") {
        actions.push("Generate Conditional Approval Certificate.");
        actions.push("Generate supporting documents.");
    }

    if (decision.docType === "final") {
        actions.push("Generate Final Approval Certificate.");
        actions.push("Generate supporting documents.");
    }

    if (decision.completeMilestones && decision.completeMilestones.length) {
        decision.completeMilestones.forEach((name) => {
            actions.push(`Complete milestone "${name}".`);
        });
    }

    if (!actions.length) {
        actions.push("No workflow action found.");
    }

    return actions;
}

function isSupportedProcessingMilestone(name) {
    return name === MILESTONE_CONDITIONAL_APPROVAL || name === MILESTONE_CTC;
}

export default function MilestoneProcessingPage() {
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);

    const [loadingText, setLoadingText] = useState("");

    const [progress, setProgress] = useState(10);

    const [loanId, setLoanId] = useState("");
    const [completedMilestoneName, setCompletedMilestoneName] = useState("");
    const [rateLockStatus, setRateLockStatus] = useState("");
    const [conditions, setConditions] = useState([]);
    const [milestones, setMilestones] = useState([]);

    const [error, setError] = useState("");

    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewTitle, setPreviewTitle] = useState("");
    const [pdfPages, setPdfPages] = useState([]);

    const [toastVisible, setToastVisible] = useState(false);
    const [toastType, setToastType] = useState("success");
    const [toastMessage, setToastMessage] = useState("");

    const activePTDs = useMemo(() => getActivePTDs(conditions), [conditions]);
    const openPTDCount = activePTDs.length;
    const hasOpenPTD = activePTDs.length > 0;
    const needsLockValidation = rateLockStatus === "Needs Validation";
    const [isApproved, setIsApproved] = useState(false);

    const decision = useMemo(
        () => buildDecision(completedMilestoneName, hasOpenPTD, needsLockValidation),
        [completedMilestoneName, hasOpenPTD, needsLockValidation]
    );

    const actions = useMemo(
        () => buildActionSummary(completedMilestoneName, hasOpenPTD, needsLockValidation),
        [completedMilestoneName, hasOpenPTD, needsLockValidation]
    );

    useEffect(() => {
        initializePage();
    }, []);

    function showToast(message, type = "success") {
        setToastMessage(message);
        setToastType(type);
        setToastVisible(true);
        setTimeout(() => setToastVisible(false), 5000);
    }

    async function initializePage() {
        setLoading(true);
        setError("");

        try {
            if (!(window.elli && window.elli.script && window.elli.script.guest)) {
                throw new Error("elli.script.guest is not available.");
            }

            window.elli.script.guest.create();

            const loanObject = await window.elli.script.getObject("loan");
            const loanPayload = await loanObject.all();
            const loan = loanPayload?.loan || loanPayload || {};
            const currentLoanId = loan?.id || await loanObject.getField("GUID");

            setLoanId(currentLoanId);
            setRateLockStatus((loan?.rateLock?.lockValidationStatus || "").trim());

            const authObject = await window.elli.script.getObject("auth");
            const authCode = await authObject.createAuthCode();
            const accessToken = await getAccessToken(authCode);

            const fields = [
                "Log.MS.CurrentMilestone"
            ];

            const data = await fieldReader(accessToken, fields, currentLoanId);

            const currentMilestoneName = (data["Log.MS.CurrentMilestone"] || "").trim();
            setCompletedMilestoneName(currentMilestoneName);

            const [milestoneData, conditionData] = await Promise.all([
                fetchLoanMilestones(accessToken, currentLoanId),
                fetchLoanConditions(accessToken, currentLoanId)
            ]);

            setMilestones(milestoneData || []);
            setConditions(conditionData || []);

            // Process only for Conditional Approval / CTC
            if (!isSupportedProcessingMilestone(currentMilestoneName)) {
                setError(
                    `Milestone processing is allowed only for "${MILESTONE_CONDITIONAL_APPROVAL}" or "${MILESTONE_CTC}". Current milestone is "${currentMilestoneName || "-"}".`
                );
                return;
            }

            // Reopen the same milestone on page load
            await reopenMilestoneByName(accessToken, currentLoanId, milestoneData || [], currentMilestoneName);

            // Refresh after reopen so UI uses latest state
            const refreshedMilestones = await fetchLoanMilestones(accessToken, currentLoanId);
            setMilestones(refreshedMilestones || []);

            await syncHostLoanWorkspace();
        } catch (err) {
            const errorCode = logError("MILESTONE_PROCESSING_PAGE_INIT_ERROR");
            logAppInsightError("Milestone Processing Page Init Error", {
                component: "MilestoneProcessingPage",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to load milestone processing page.`
            });

            console.error("MilestoneProcessingPage: initializePage failed", err);
            setError("Failed to load milestone processing details.");
        } finally {
            setLoading(false);
        }
    }

    async function ensureLockValidationCondition(token, currentLoanId, latestConditions) {
        const existingCondition = getLockValidationCondition(latestConditions);

        if (!existingCondition) {
            await addLoanCondition(token, currentLoanId, {
                title: CONDITION_TITLE_LOCK_VALIDATION,
                conditionType: "Underwriting",
                priorTo: "Docs"
            });
            return;
        }

        const currentStatus = (existingCondition.status || "").trim().toLowerCase();

        if (currentStatus === "cleared") {
            await addConditionTracking(token, currentLoanId, existingCondition.id, {
                status: "Cleared",
                isChecked: false
            });
        }
    }

    async function syncHostLoanWorkspace() {
        try {
            const loanObj = await window.elli.script.getObject("loan");
            await loanObj.merge();
            await loanObj.calculate();
        } catch (e) {
            console.warn("MilestoneProcessingPage: merge/calculate failed:", e);
        }
    }

    async function reopenMilestoneByName(token, currentLoanId, currentMilestones, milestoneName) {
        const milestone = findMilestoneByName(currentMilestones, milestoneName);

        if (!milestone) {
            console.warn(`MilestoneProcessingPage: milestone "${milestoneName}" not found`);
            return;
        }

        if (milestone.doneIndicator === false) {
            console.log(`Milestone "${milestoneName}" is already reopened.`);
            return;
        }

        await updateLoanMilestone(token, currentLoanId, milestone.id, {
            doneIndicator: false
        });
    }

    async function completeMilestoneByName(token, currentLoanId, currentMilestones, milestoneName) {
        const milestone = findMilestoneByName(currentMilestones, milestoneName);

        if (!milestone) {
            console.warn(`Milestone "${milestoneName}" not found`);
            return;
        }

        if (milestone.doneIndicator === true) {
            console.log(`Milestone "${milestoneName}" already completed → reopening first`);

            await updateLoanMilestone(token, currentLoanId, milestone.id, {
                doneIndicator: false
            });

            currentMilestones = await fetchLoanMilestones(token, currentLoanId);

            const updatedMilestone = findMilestoneByName(currentMilestones, milestoneName);

            if (!updatedMilestone) {
                throw new Error(`Milestone "${milestoneName}" not found after reopening.`);
            }

            await updateLoanMilestone(token, currentLoanId, updatedMilestone.id, {
                doneIndicator: true
            });

            return;
        }

        await updateLoanMilestone(token, currentLoanId, milestone.id, {
            doneIndicator: true
        });
    }

    async function callGenerateApi(docType) {
        const authObj = await window.elli.script.getObject("auth");
        const authCode = await authObj.createAuthCode();
        const endpoint = `${API_DOMAIN}/api/ApprovalCertificate/v2/generate`;

        const response = await fetch(endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                loanId,
                authCode,
                docType
            })
        });

        if (!response.ok) {
            const text = await response.text().catch(() => response.statusText || "Unknown error");
            throw new Error(`Failed to generate ${docType} certificate: ${response.status} - ${text}`);
        }
    }

    async function renderPDF(blobUrl) {
        const loadingTask = pdfjsLib.getDocument({
            url: blobUrl,
            disableWorker: true
        });

        const pdf = await loadingTask.promise;
        const pageList = [];

        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const viewport = page.getViewport({ scale: 1.2 });

            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            canvas.width = viewport.width;
            canvas.height = viewport.height;

            await page.render({ canvasContext: ctx, viewport }).promise;
            pageList.push(canvas.toDataURL());
        }

        return pageList;
    }

    async function previewGeneratedPdf() {
        try {
            if (!decision.docType) {
                showToast("Preview not available for this scenario.", "error");
                return;
            }

            setPreviewLoading(true);
            setPreviewVisible(true);
            setPdfPages([]);

            setPreviewTitle(
                decision.docType === "conditional"
                    ? "Conditional Approval Certificate Preview"
                    : "Final Approval Certificate Preview"
            );

            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();

            const endpoint = `${API_DOMAIN}/api/ApprovalCertificate/v2/preview-pdf-only`;

            const response = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    loanId,
                    authCode,
                    docType: decision.docType
                })
            });

            if (!response.ok) {
                const errText = await response.text().catch(() => response.statusText || "Unknown error");
                throw new Error(errText);
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const pages = await renderPDF(blobUrl);
            setPdfPages(pages);
        } catch (err) {
            console.error("MilestoneProcessingPage: previewGeneratedPdf error:", err);
            showToast(`Unable to preview document. ${err?.message || ""}`, "error");
        } finally {
            setPreviewLoading(false);
        }
    }

    async function refreshData() {
        const authObj = await window.elli.script.getObject("auth");
        const authCode = await authObj.createAuthCode();
        const token = await getAccessToken(authCode);

        const [milestoneData, conditionData] = await Promise.all([
            fetchLoanMilestones(token, loanId),
            fetchLoanConditions(token, loanId)
        ]);

        setMilestones(milestoneData || []);
        setConditions(conditionData || []);
    }

    async function handleApprove() {
        if (!loanId) {
            showToast("Loan ID not found.", "error");
            return;
        }

        try {
            setProcessing(true);
            setProgress(10);
            setLoadingText("Starting approval process...");

            const authObj = await window.elli.script.getObject("auth");
            const authCode = await authObj.createAuthCode();
            const token = await getAccessToken(authCode);

            setLoadingText("Fetching milestone and condition details...");
            setProgress(20);

            let latestConditions = await fetchLoanConditions(token, loanId);
            let latestMilestones = await fetchLoanMilestones(token, loanId);

            if (decision.ensureLockValidationCondition) {
                setLoadingText('Creating or re-opening "Lock Validation Required" condition...');
                setProgress(35);

                await ensureLockValidationCondition(token, loanId, latestConditions);

                latestConditions = await fetchLoanConditions(token, loanId);
                setConditions(latestConditions);
            }

            if (decision.docType) {
                setLoadingText(
                    decision.docType === "conditional"
                        ? "Generating Conditional Approval Certificate..."
                        : "Generating Final Approval Certificate..."
                );
                setProgress(55);

                await callGenerateApi(decision.docType);

                setLoadingText("Generating supporting documents...");
                setProgress(70);

                await generateAdditionalDocuments(loanId);
            }

            if (decision.completeMilestones.length) {
                latestMilestones = await fetchLoanMilestones(token, loanId);

                setLoadingText("Updating milestone status...");
                setProgress(82);

                for (const milestoneName of decision.completeMilestones) {
                    await completeMilestoneByName(token, loanId, latestMilestones, milestoneName);
                }
            }

            setLoadingText("Refreshing workspace...");
            setProgress(92);

            await syncHostLoanWorkspace();
            await refreshData();

            setLoadingText("Completed.");
            setProgress(100);

            showToast("Milestone processing completed successfully.", "success");
            setIsApproved(true);
        } catch (err) {
            const errorCode = logError("MILESTONE_PROCESSING_APPROVE_ERROR");
            logAppInsightError("Milestone Processing Approve Error", {
                component: "MilestoneProcessingPage",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed during milestone processing approval.`
            });

            console.error("MilestoneProcessingPage: handleApprove failed", err);
            showToast(err?.message || "Failed to process milestone approval.", "error");
        } finally {
            setProcessing(false);
            setLoadingText("");
        }
    }

    return (
        <>
            <LoadingOverlay loading={loading} text="Loading..." />

            <div className="min-h-screen bg-gray-50 p-6">
                <div className="max-w-6xl mx-auto">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        {error ? (
                            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
                                {error}
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {processing && (
                                    <section className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                                        <div className="flex items-center justify-between gap-4 mb-3">
                                            <h2 className="text-lg font-semibold text-blue-900">
                                                Processing Milestone Approval
                                            </h2>
                                            <span className="text-sm font-medium text-blue-800">
                                                {progress}%
                                            </span>
                                        </div>

                                        <div className="w-full h-3 bg-blue-100 rounded-full overflow-hidden mb-3">
                                            <div
                                                className="h-full bg-[#2080C3] transition-all duration-700 ease-out"
                                                style={{ width: `${progress}%` }}
                                            />
                                        </div>

                                        <p className="text-sm text-blue-900">{loadingText}</p>
                                    </section>
                                )}

                                {!loading && !error && (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <InfoCard
                                                label="Milestone"
                                                value={completedMilestoneName || "-"}
                                            />
                                            <InfoCard
                                                label="Lock Validation Status"
                                                value={rateLockStatus || "PASS"}
                                                isPass={!needsLockValidation}
                                            />
                                            <InfoCard
                                                label="Open PTD Conditions"
                                                value={
                                                    openPTDCount > 0
                                                        ? `${openPTDCount} pending`
                                                        : "All cleared"
                                                }
                                                isPass={!hasOpenPTD}
                                            />
                                        </div>

                                        <section className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                                            <h2 className="text-lg font-semibold text-blue-900 mb-3">
                                                What this page will do
                                            </h2>

                                            <ul className="space-y-2">
                                                {actions.map((action, index) => (
                                                    <li
                                                        key={`${action}-${index}`}
                                                        className="flex items-start gap-2 text-sm text-blue-900"
                                                    >
                                                        <span>•</span>
                                                        <span>{action}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </section>

                                        <div className="flex justify-end gap-3 pt-2">
                                            <Tooltip title="Preview approval certificate">
                                                <button
                                                    className="btn-preview flex items-center justify-center gap-2 text-white bg-[#2080C3] hover:bg-[#5DA5D9] rounded px-4 py-2 font-semibold disabled:opacity-50"
                                                    onClick={previewGeneratedPdf}
                                                    disabled={!decision.docType || processing}
                                                >
                                                    <Eye size={16} />
                                                    <span className="leading-none">Preview</span>
                                                </button>
                                            </Tooltip>

                                            <Tooltip title="Approve milestone processing">
                                                <button
                                                    className="btn-custom rounded px-4 py-2 font-semibold text-white bg-[#2080C3] hover:bg-[#5DA5D9] disabled:opacity-50"
                                                    onClick={handleApprove}
                                                    disabled={processing || isApproved}
                                                >
                                                    Approve
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Toast
                visible={toastVisible}
                type={toastType}
                message={toastMessage}
                onClose={() => setToastVisible(false)}
            />

            <Modal
                open={previewVisible}
                onCancel={() => setPreviewVisible(false)}
                footer={null}
                width="99vw"
                centered
                styles={{
                    body: {
                        padding: "0",
                        backgroundColor: "#f5f7fa",
                        borderRadius: "8px",
                        height: "82vh",
                        overflowY: "auto"
                    },
                    header: {
                        fontSize: "18px",
                        fontWeight: 600,
                        paddingBottom: "10px",
                        background: "#ffffff",
                        borderBottom: "1px solid #e5e7eb"
                    },
                    content: {
                        borderRadius: "10px",
                        overflow: "hidden",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
                    },
                    mask: {
                        backgroundColor: "rgba(0,0,0,0.45)"
                    }
                }}
                title={previewTitle}
            >
                {previewLoading ? (
                    <div className="flex justify-center items-center h-full">
                        <Spin size="large" />
                    </div>
                ) : (
                    <div className="flex flex-col items-center bg-[#f5f7fa] p-4">
                        {pdfPages.map((src, index) => (
                            <img
                                key={index}
                                src={src}
                                alt={`Page ${index + 1}`}
                                style={{
                                    width: "100%",
                                    maxWidth: "1000px",
                                    height: "auto",
                                    marginBottom: "16px",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                                }}
                            />
                        ))}
                    </div>
                )}
            </Modal>
        </>
    );
}

function InfoCard({ label, value, isPass }) {
    const valueClass =
        isPass === undefined
            ? "text-gray-900"
            : isPass
                ? "text-green-600 font-semibold"
                : "text-red-600 font-semibold";

    return (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {label}
            </div>
            <div className={`text-sm mt-1 break-all ${valueClass}`}>
                {value}
            </div>
        </div>
    );
}